import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { useAuthContext } from './contexts/AuthContext';
import { useUserProfileContext } from './contexts/UserProfileContext';
import { useCommunityContext } from './contexts/CommunityContext';
import { useEventsContext } from './contexts/EventsContext';
import { useActivitiesContext } from './contexts/ActivitiesContext';
import { useNewsContext } from './contexts/NewsContext';
import { useNotificationsContext } from './contexts/NotificationsContext';
import { dataFlowLog } from './utils/dataFlowLogger';

const defaultAppContext = {
  handleSignOut: () => {},
  setLoading: () => {},
  loading: false,
  policyChanged: false,
  setPolicyChanged: () => {},
  homeHeroReady: false,
  setHomeHeroReady: () => {},
};

const AppContext = createContext(defaultAppContext);

export const useAppContext = () => useContext(AppContext);

export const AppContextProvider = ({ children }) => {
  const { signOut } = useAuthContext() || {};
  const { setProfile, setFamily } = useUserProfileContext() || {};
  const { setCommunity, setStats, setLsa } = useCommunityContext() || {};
  const { setEvents } = useEventsContext() || {};
  const { setActivities } = useActivitiesContext() || {};
  const { setNewsFeed } = useNewsContext() || {};
  const { setNotifications } = useNotificationsContext() || {};
  const [policyChanged, setPolicyChanged] = useState(false);
  const [loadingState, setLoadingState] = useState(false);
  const setLoading = useCallback((next) => {
    setLoadingState((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      dataFlowLog('app-context.setLoading', { prev, next: resolved });
      return resolved;
    });
  }, []);
  const loading = loadingState;
  const [homeHeroReady, setHomeHeroReady] = useState(false);
  const handleSignOut = useCallback((afterSignOut) => {
    dataFlowLog('app-context.sign-out.start');
    setLoading(true);
    if (typeof signOut === 'function') signOut();
    if (typeof setProfile === 'function') setProfile(null);
    if (typeof setFamily === 'function') setFamily(null);
    if (typeof setCommunity === 'function') setCommunity(null);
    if (typeof setStats === 'function') setStats(null);
    if (typeof setLsa === 'function') setLsa([]);
    if (typeof setActivities === 'function') setActivities([]);
    if (typeof setEvents === 'function') setEvents([]);
    if (typeof setNewsFeed === 'function') setNewsFeed([]);
    if (typeof setNotifications === 'function') setNotifications([]);
    if (afterSignOut) afterSignOut();
    setLoading(false);
    setHomeHeroReady(false);
    dataFlowLog('app-context.sign-out.done');
  }, [
    setActivities,
    setCommunity,
    setEvents,
    setFamily,
    setHomeHeroReady,
    setLsa,
    setLoading,
    setNewsFeed,
    setNotifications,
    setProfile,
    setStats,
    signOut,
  ]);

  useEffect(() => {
    dataFlowLog('app-context.updated', {
      loading,
      homeHeroReady,
      policyChanged,
    });
  }, [homeHeroReady, loading, policyChanged]);

  const value = useMemo(() => ({
    handleSignOut,
    setLoading,
    loading,
    // Policy change flag and setter
    policyChanged,
    setPolicyChanged,
    homeHeroReady,
    setHomeHeroReady,
  }), [
    handleSignOut,
    homeHeroReady,
    loading,
    policyChanged,
    setHomeHeroReady,
    setLoading,
    setPolicyChanged,
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
